<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import { MaximizeIcon, MinimizeIcon } from 'vue-tabler-icons';
import ResourceList from '@/components/ResourceList.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const draft = ref({
  namespace: store.getters['users/user'].namespaces[0]?.namespace || undefined,
  name: '',
  description: '',
  models: [],
});

const isModelsSectionOpen = ref(true);
const errorMessage = ref('');
const selectedModel = ref(route.query.model);
const progressStatus = ref('');
const inProgress = ref(false);
const user = computed(() => store.getters['users/user']);

async function save() {
  errorMessage.value = '';
  progressStatus.value = '';
  inProgress.value = true;
  try {
    if (!selectedModel.value) {
      errorMessage.value = 'Select the Model from the list';
      return;
    }
    await store.dispatch('ci/createCItem', draft.value);
    await store.dispatch('ci/createCIDraft', {
      ...draft.value,
      description: 'Initial Revision',
      models: [selectedModel.value],
    });
    progressStatus.value = 'CI Draft Created';
    router.push(`/configurable-items/${draft.value.namespace}:${draft.value.name}`);
    progressStatus.value = '';
  } catch (error) {
    errorMessage.value = error.message;
  } finally {
    inProgress.value = false;
  }
}
</script>

<template>
  <section class="flex w-full flex-col px-8 py-6 text-black">
    <h2 class="mx-2 mb-4 text-3xl font-medium">{{ $route.name }}</h2>
    <form class="flex flex-grow flex-col gap-4 px-2" name="create-draft" @submit.prevent="save">
      <div class="mb-3 grid w-full grid-cols-2 grid-rows-2 items-baseline gap-y-2 gap-x-8 text-xs">
        <div class="col-span-1 row-span-1 flex flex-1 flex-col">
          <label class="py-2 font-semibold text-gray-600"> Title <abbr title="required">*</abbr></label>
          <input
            v-model.trim="draft.name"
            placeholder="CI Name"
            class="outline-none block h-10 w-full rounded-lg border border-gray-100 bg-white px-4 text-gray-800"
            required
            type="text"
            name="name"
          />
        </div>
        <div class="col-span-1 row-span-2 mb-1 w-full flex-auto space-y-2 text-xs">
          <label class="py-2 font-semibold text-gray-600">Description</label>
          <textarea
            id=""
            v-model.trim="draft.description"
            name="description"
            class="outline-none block h-32 w-full rounded-lg border border-gray-100 bg-white px-4 py-4 text-gray-800"
            placeholder="Enter the description"
            spellcheck="false"
          ></textarea>
        </div>
        <div class="col-span-1 row-span-1 flex flex-1 flex-col">
          <label class="py-2 font-semibold text-gray-600">Namespace <abbr title="required">*</abbr></label>
          <select
            id="integration_city_id"
            v-model="draft.namespace"
            class="block h-10 w-full rounded-lg border border-gray-100 bg-white px-4 text-gray-800"
            required
            name="namespace"
          >
            <option v-for="{ namespace } in user.namespaces" :key="namespace" :value="namespace">
              {{ namespace }}
            </option>
          </select>
        </div>
      </div>

      <section class="rounded-xl border-b bg-gray-50 pb-3" style="max-height: 34rem">
        <header
          class="top-0 flex items-center justify-between rounded-t-xl bg-white px-3 pt-4 pb-2"
          @click="isModelsSectionOpen = !isModelsSectionOpen"
        >
          <h1 class="mx-2 text-lg">Select Model <abbr title="required">*</abbr></h1>
          <div class="create-draft__selected-model flex flex-col items-end text-xs">
            <small>Selected Model Id</small> {{ selectedModel || '-' }}
          </div>

          <minimize-icon v-if="isModelsSectionOpen" />
          <maximize-icon v-if="!isModelsSectionOpen" />
        </header>

        <resource-list
          v-show="isModelsSectionOpen"
          v-model:selected="selectedModel"
          resource-type="model"
          resource-label="model"
          :is-selectable="true"
          class="mt-8"
        />
      </section>

      <p class="my-3 text-right text-xs text-red-500">
        Required fields are marked with an asterisk <abbr title="Required field">*</abbr>
      </p>

      <div v-if="errorMessage || progressStatus" class="flex justify-end p-1">
        <div
          class="text-teal inline-flex items-center rounded-full bg-white p-2 text-sm leading-none shadow"
          :class="{ 'text-red-500': errorMessage, 'text-green-500': progressStatus }"
        >
          <span
            class="inline-flex h-6 items-center justify-center rounded-full px-3 text-white"
            :class="{ 'bg-red-500': errorMessage, 'bg-blue-500': progressStatus }"
            >{{ errorMessage ? 'Error' : 'Success' }}
          </span>
          <span class="inline-flex px-2">{{ errorMessage ? errorMessage : progressStatus }}</span>
        </div>
      </div>

      <div class="mt-5 flex justify-end gap-x-4 text-right">
        <button
          role="button"
          type="button"
          :disabled="inProgress"
          class="
            mb-2
            rounded-full
            border
            bg-white
            px-5
            py-2
            text-sm
            font-medium
            tracking-wider
            text-gray-600
            shadow-sm
            hover:bg-white hover:shadow-lg
            disabled:cursor-not-allowed disabled:opacity-25
          "
        >
          Cancel
        </button>
        <input
          value="Save"
          type="submit"
          :disabled="inProgress"
          class="
            mb-2
            cursor-pointer
            rounded-full
            bg-brand-500
            px-5
            py-2
            text-sm
            font-medium
            tracking-wider
            text-white
            shadow-sm
            hover:bg-brand-600 hover:shadow-lg
            disabled:cursor-not-allowed disabled:opacity-25
          "
        />
      </div>
    </form>
  </section>
</template>
